import { ElementType, FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

const keyPrefix = 'components.form-outputs.PasswordRuleRow';

/**
 * `failed` no es lo mismo que `pending`: pendiente es «todavía no», y no
 * cumplida es «lo intentaste enviar así». Se distinguen para no pintar de rojo
 * una regla que el vendedor apenas va a escribir.
 */
export type PasswordRuleState = 'pending' | 'met' | 'failed';

type Props = {
  children: ReactNode;
  component?: ElementType;
  state: PasswordRuleState;
};

/**
 * Un renglón de requisito de contraseña: icono, color y texto. Lo comparten el
 * checklist de la política y el aviso de coincidencia de la confirmación, para
 * que los dos se vean igual y para que un cambio de color o de icono no haya
 * que hacerlo dos veces.
 *
 * El estado se anuncia con `titleAccess` además de con el color, para que no
 * dependa solo de este último.
 */
export const PasswordRuleRow: FC<Props> = ({
  children,
  component = 'li',
  state,
}): JSX.Element => {
  const { t } = useTranslation('translation', { keyPrefix });
  const theme = useTheme();

  const Icon = state === 'met' ? CheckCircleIcon : RadioButtonUncheckedIcon;
  const color = {
    met: theme.palette.success.main,
    failed: theme.palette.error.main,
    pending: theme.palette.text.secondary,
  }[state];

  return (
    <Stack
      component={component}
      direction='row'
      spacing={1}
      alignItems='center'
    >
      <Icon fontSize='small' titleAccess={t(state)} sx={{ color }} />
      <Typography variant='body2' sx={{ color }}>
        {children}
      </Typography>
    </Stack>
  );
};
